import ReactDOM from "react-dom"
import React, { Suspense, useRef, useEffect, useState, useMemo } from "react"
import * as THREE from "three"
import { Canvas, useFrame, useThree } from "react-three-fiber"
import { useHover, useDrag } from "react-use-gesture"
import { useSpring, a } from "react-spring/three"
import { CustomShader } from "./shaders/CustomShader"
//import { OrbitControls } from "drei"
//import { FadeShader } from "./shaders/FadeShader"

import "./styles.css"

function ShaderMaterial(props) {
  const ref = useRef()
  const mouse = useMousePosition()
  const size = useWindowSize()
  const uniforms = useMemo(() => THREE.UniformsUtils.merge([THREE.UniformsLib.lights, CustomShader.uniforms]), [])

  // this works, but is not dependent on props
  useFrame((state) => {
    ref.current.uniforms.effectFactor.value = props.effectFactor
    ref.current.uniforms.dispFactor.value = props.dispFactor

    ref.current.uniforms.windowWidth.value = size.width + mouse.x
    ref.current.uniforms.windowHeight.value = size.height + mouse.y
  })

  return (
    <shaderMaterial
      ref={ref}
      attach="material"
      uniforms={uniforms}
      vertexShader={CustomShader.vertexShader}
      fragmentShader={CustomShader.fragmentShader}
      lights={true}
    />
  )
}

function Sphere({ radius }) {
  const ref = useRef()

  const [hovered, setHover] = useState(false)

  useFrame(() => {
    ref.current.rotation.x = ref.current.rotation.y += 0.003
  })

  return (
    <mesh
      ref={ref}
      onClick={(e) => console.log("click")}
      onPointerMove={(e) => setHover(true)}
      onPointerOut={(e) => setHover(false)}>
      <sphereBufferGeometry attach="geometry" args={[radius, 64, 64]} />
      <ShaderMaterial effectFactor={0.75} dispFactor={2} hovered={hovered} />
    </mesh>
  )
}

function BallDraggable({ position, radius = 1 }) {
  const { size, viewport } = useThree()
  const aspect = size.width / viewport.width

  const [spring, set] = useSpring(() => ({
    scale: [1, 1, 1],
    position: [...position],
    rotation: [0, 0, 0],
    config: { mass: 20, friction: 80, tension: 800 }
  }))

  const bindDrag = useDrag(
    ({ offset: [x, y], vxvy: [vx, vy], down, ...props }) =>
      set({ position: [x / aspect, -y / aspect, position[2]], rotation: [0, 0, 0] }),
    { pointerEvents: true }
  )

  const bindHover = useHover(({ hovering }) => set({ scale: hovering ? [1.2, 1.2, 1.2] : [1, 1, 1] }), {
    pointerEvents: true
  })

  return (
    <a.group {...spring} {...bindHover()} {...bindDrag()}>
      <mesh castShadow receiveShadow>
        <sphereBufferGeometry attach="geometry" args={[radius, 32, 32]} />
        <ShaderMaterial effectFactor={-2} dispFactor={12} overed={false} />
      </mesh>
    </a.group>
  )
}

ReactDOM.render(
  <Canvas camera={{ position: [0, 0, 3.5], near: 0.01, far: 100, fov: 30 }}>
    <ambientLight color="#ffffee" intensity={0.1} />
    {/* <spotLight color="#0000ff" intensity={0.3} position={[-30, -30, 15]} angle={1.2} penumbra={1} castShadow />
    <spotLight color="#ff0033" intensity={0.6} position={[30, 30, 10]} angle={1.2} penumbra={1} castShadow />
    <spotLight color="#ff00ee" intensity={0.1} position={[30, -30, 10]} angle={1.0} penumbra={1} castShadow /> */}
    <Suspense fallback={null}>
      <BallDraggable position={[0, 0, 0.7]} radius={0.25} />
      <Sphere radius={0.6} />
    </Suspense>
  </Canvas>,
  document.getElementById("root")
)

const useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState({ x: null, y: null })

  const updateMousePosition = (ev) => {
    setMousePosition({ x: ev.clientX, y: ev.clientY })
  }

  useEffect(() => {
    window.addEventListener("mousemove", updateMousePosition)

    return () => window.removeEventListener("mousemove", updateMousePosition)
  }, [])

  return mousePosition
}

function useWindowSize() {
  const isClient = typeof window === "object"

  function getSize() {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined
    }
  }

  const [windowSize, setWindowSize] = useState(getSize)

  useEffect(() => {
    if (!isClient) {
      return false
    }

    function handleResize() {
      setWindowSize(getSize())
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, []) // Empty array ensures that effect is only run on mount and unmount

  return windowSize
}
